import { watchEffect } from 'vue'

import { createRouter, createWebHistory } from 'vue-router'
import { routes as autoRoutes, handleHotUpdate } from 'vue-router/auto-routes'

import auth0, { ALLOWED_ORG_ID } from '#app/services/auth0'
import { token } from '#app/store/auth'
import { toast } from '#ui/sonner'
import { errorMessage, isStaleChunk } from '#utils/errors'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: autoRoutes,
})

function watchEffectOnceAsync(watcher: () => boolean): Promise<void> {
  return new Promise((resolve) => {
    const stopWatch = watchEffect(() => {
      if (watcher()) {
        resolve()
        stopWatch()
      }
    })
  })
}

router.beforeEach(async (to) => {
  const routeRequiresAuth = to.matched.some((record) => record.meta.requiresAuth)
  if (!routeRequiresAuth) return true

  if (auth0.isLoading.value) await watchEffectOnceAsync(() => !auth0.isLoading.value)

  try {
    await auth0.checkSession()
    token.value = await auth0.getAccessTokenSilently()
  } catch {
    await auth0.loginWithRedirect({ appState: { target: to.path } })
    return false
  }

  if (!auth0.isAuthenticated.value) {
    await auth0.loginWithRedirect({ appState: { target: to.path } })
    return false
  }

  if (auth0.idTokenClaims.value?.org_id !== ALLOWED_ORG_ID) {
    token.value = null
    await auth0.logout({ logoutParams: { returnTo: window.location.origin } })
    return false
  }

  return true
})

const RELOADED_FOR = 'admin.reloadedForStaleChunk'

router.onError((error, to) => {
  if (isStaleChunk(error) && sessionStorage.getItem(RELOADED_FOR) !== to.fullPath) {
    sessionStorage.setItem(RELOADED_FOR, to.fullPath)
    window.location.assign(to.fullPath)
    return
  }
  toast.error(errorMessage(error), { description: `Could not open ${to.fullPath}` })
})

router.afterEach((to) => {
  sessionStorage.removeItem(RELOADED_FOR)
  const title = to.meta.title
  document.title = title ? `${title} | Deducta Admin` : 'Deducta Admin'
})

if (import.meta.hot) {
  handleHotUpdate(router)
}

export default router
